/**
 * Word文档生成服务
 * 使用 docx.js 在前端生成Word文档
 * 完全复现 docxtpl 版本的公文排版逻辑
 */
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel, Footer, PageNumber, 
         Table, TableRow, TableCell, WidthType, HeightRule, VerticalAlign, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

class WordGeneratorService {

  /**
   * 将字符单位转换为 twips
   * Word 中 1字符 ≈ 字号pt值，1pt = 20 twips
   * @param {number} charCount - 字符数（如 2 表示2字符）
   * @param {number} fontSize - 字号（半磅值，如16pt=32，默认16pt）
   * @returns {number} - twips 值
   */
  charToTwips(charCount, fontSize = 32) {
    const pt = (fontSize / 2) * charCount;  // 转换为pt
    return Math.round(pt * 20);  // pt 转 twips
  }

  /**
   * 数字转换为中文数字
   */
  numberToChinese(num) {
    const chineseNumbers = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
    if (num <= 10) {
      return chineseNumbers[num];
    } else if (num < 20) {
      return '十' + (num === 10 ? '' : chineseNumbers[num - 10]);
    } else if (num < 100) {
      const tens = Math.floor(num / 10);
      const ones = num % 10;
      return chineseNumbers[tens] + '十' + (ones === 0 ? '' : chineseNumbers[ones]);
    }
    return num.toString();
  }

  /**
   * 判断字符是否为西文字符（应使用Times New Roman）
   * 包括：英文、数字、西文标点、数学符号、度量符号等
   */
  isWesternChar(char) {
    const code = char.charCodeAt(0);
    // 基本拉丁字母和数字：0-9, A-Z, a-z
    if ((code >= 0x0030 && code <= 0x0039) ||  // 数字
        (code >= 0x0041 && code <= 0x005A) ||  // 大写字母
        (code >= 0x0061 && code <= 0x007A)) {  // 小写字母
      return true;
    }
    // 西文标点和符号（ASCII 0x21-0x2F, 0x3A-0x40, 0x5B-0x60, 0x7B-0x7E）
    if ((code >= 0x0021 && code <= 0x002F) ||  // ! " # $ % & ' ( ) * + , - . /
        (code >= 0x003A && code <= 0x0040) ||  // : ; < = > ? @
        (code >= 0x005B && code <= 0x0060) ||  // [ \ ] ^ _ `
        (code >= 0x007B && code <= 0x007E)) {  // { | } ~
      return true;
    }
    // 拉丁扩展字符（带重音符号等）
    if ((code >= 0x00C0 && code <= 0x00FF) ||  // Latin-1 Supplement
        (code >= 0x0100 && code <= 0x024F)) {  // Latin Extended-A/B
      return true;
    }
    // 常用数学和度量符号
    const westernSymbols = [
      '‰', '°', '′', '″', '±', '×', '÷',  // 度量和数学符号
      '—', '–', '―',                           // 破折号
      '…',                                      // 省略号（西文）
      '←', '→', '↑', '↓',                      // 箭头
      '≤', '≥', '≠', '≈', '∞',                 // 数学比较符号
      '℃', '℉',                                 // 温度符号
      '￥', '＄', '€', '£', '¢',               // 货币符号（部分）
      '§', '¶', '©', '®', '™', '№'            // 特殊符号
    ];
    if (westernSymbols.includes(char)) {
      return true;
    }
    // Unicode数学运算符区段
    if (code >= 0x2200 && code <= 0x22FF) {  // Mathematical Operators
      return true;
    }
    return false;
  }

  /**
   * 创建带正确字体的TextRun数组（西文字符使用Times New Roman）
   */
  createTextRunsWithFont(text, options = {}) {
    const { size = 32, font = "仿宋_GB2312", bold = false } = options;
    const textRuns = [];
    
    // 按字符类型分段：连续的西文字符或连续的中文字符分别归组
    let currentIsWestern = null;
    let currentSegment = '';
    
    for (const char of text) {
      const isWestern = this.isWesternChar(char);
      
      if (currentIsWestern === null) {
        // 第一个字符，初始化
        currentIsWestern = isWestern;
        currentSegment = char;
      } else if (isWestern === currentIsWestern) {
        // 同类型字符，追加到当前段
        currentSegment += char;
      } else {
        // 类型切换，先保存当前段
        textRuns.push(new TextRun({
          text: currentSegment,
          size,
          font: currentIsWestern ? {
            eastAsia: font,
            ascii: "Times New Roman",
            hAnsi: "Times New Roman",
            cs: "Times New Roman"
          } : {
            eastAsia: font,
            ascii: "Times New Roman",
            hAnsi: "Times New Roman",
            cs: "Times New Roman"
          },
          bold
        }));
        // 开始新段
        currentIsWestern = isWestern;
        currentSegment = char;
      }
    }
    
    // 保存最后一段
    if (currentSegment) {
      textRuns.push(new TextRun({
        text: currentSegment,
        size,
        font: currentIsWestern ? {
          eastAsia: font,
          ascii: "Times New Roman",
          hAnsi: "Times New Roman",
          cs: "Times New Roman"
        } : {
          eastAsia: font,
          ascii: "Times New Roman",
          hAnsi: "Times New Roman",
          cs: "Times New Roman"
        },
        bold
      }));
    }
    
    return textRuns;
  }

  /**
   * 规范化空白字符
   */
  normalizeWhitespace(text) {
    // 将不间断空格等特殊空白字符替换为普通空格
    return text
      .replace(/\u00a0/g, ' ')
      .replace(/\u2000/g, ' ')
      .replace(/\u2001/g, ' ')
      .replace(/\u2002/g, ' ')
      .replace(/\u2003/g, ' ')
      .replace(/\u2004/g, ' ')
      .replace(/\u2005/g, ' ')
      .replace(/\u2006/g, ' ')
      .replace(/\u2007/g, ' ')
      .replace(/\u2008/g, ' ')
      .replace(/\u2009/g, ' ')
      .replace(/\u200a/g, ' ')
      .replace(/\u202f/g, ' ')
      .replace(/\u205f/g, ' ')
      .replace(/\u3000/g, ' ');
  }

  /**
   * 递归提取文本及其格式信息
   */
  extractTextWithFormat(node, isBold = false) {
    const segments = [];
    
    if (node.nodeType === Node.TEXT_NODE) {
      const text = this.normalizeWhitespace(node.textContent.trim());
      if (text) {
        segments.push({ text, isBold });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // 检查是否是加粗标签
      const nodeIsBold = isBold || ['STRONG', 'B'].includes(node.tagName);
      
      for (const child of node.childNodes) {
        segments.push(...this.extractTextWithFormat(child, nodeIsBold));
      }
    }
    
    return segments;
  }

  /**
   * 解析HTML内容为docx段落（完全复现 docxtpl 版本的逻辑）
   */
  parseHtmlContent(htmlContent) {
    const paragraphs = [];
    const counters = { 1: 0, 2: 0, 3: 0 };
    
    // 创建临时div来解析HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    
       // 遍历所有段落（包括可能的标题标签和div）
    const elements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6,h7, h8, h9, h10, h11, h12, div');

    
    elements.forEach(element => {
      // 提取带格式的文本段
      const formattedSegments = this.extractTextWithFormat(element);
      
      if (formattedSegments.length === 0) return;
      
      // 合并为完整文本以判断标题
      const fullText = formattedSegments.map(seg => seg.text).join('');
      
      // 处理多行文本中的标题（按行处理）
      const lines = fullText.split('\n');
      
      // 构建格式映射：记录每个字符位置是否加粗
      const charBoldMap = {};
      let charIndex = 0;
      for (const seg of formattedSegments) {
        for (const char of seg.text) {
          charBoldMap[charIndex] = seg.isBold;
          charIndex++;
        }
      }
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;
        
        // 计算当前行在全文中的起始位置
        const lineStart = fullText.indexOf(trimmedLine);
        
        let currentLevel = null;
        let currentFont = '仿宋_GB2312';
        let currentBold = false;
        let titleLengthDiff = 0; // 记录标题长度变化（原长度 - 新长度）
        let processedLine = trimmedLine;
        
        // 一级标题：一、二、三...
        const match1 = trimmedLine.match(/^([一二三四五六七八九十]+、)/);
        if (match1) {
          currentLevel = 1;
          counters[1]++;
          const originalTitle = match1[1];
          const newTitle = `${this.numberToChinese(counters[1])}、`;
          titleLengthDiff = originalTitle.length - newTitle.length;
          processedLine = newTitle + trimmedLine.substring(originalTitle.length);
          counters[2] = 0;
          counters[3] = 0;
          currentFont = '黑体';
          currentBold = false;
        }
        // 二级标题：（一）（二）（三）...
        else {
          const match2 = trimmedLine.match(/^(（[一二三四五六七八九十]+）|\([一二三四五六七八九十]+\))/);
          if (match2) {
            currentLevel = 2;
            counters[2]++;
            const originalTitle = match2[1];
            const newTitle = `（${this.numberToChinese(counters[2])}）`;
            titleLengthDiff = originalTitle.length - newTitle.length;
            processedLine = newTitle + trimmedLine.substring(originalTitle.length);
            counters[3] = 0;
            currentFont = '楷体_GB2312';
            currentBold = true;
          }
          // 三级标题：1. 2. 3....
          else {
            const match3 = trimmedLine.match(/^([1-9]\d*\.)/);
            if (match3) {
              currentLevel = 3;
              counters[3]++;
              const originalTitle = match3[1];
              const newTitle = `${counters[3]}.`;
              titleLengthDiff = originalTitle.length - newTitle.length;
              processedLine = newTitle + trimmedLine.substring(originalTitle.length);
              currentFont = '仿宋_GB2312';
              currentBold = true;
            }
          }
        }
        
        // 对于标题行，分割标题部分和正文部分
        const textRuns = [];
        if (currentLevel) {
          const dotPos = processedLine.indexOf('。');
          if (dotPos !== -1) {
            // 标题部分（到句号为止）
            const titlePart = processedLine.substring(0, dotPos + 1);
            // 正文部分（句号之后）
            const bodyPart = processedLine.substring(dotPos + 1);
            
            // 标题部分使用标题格式
            textRuns.push(...this.createTextRunsWithFont(titlePart, {
              size: 32,
              font: currentFont,
              bold: currentBold
            }));
            
            // 正文部分保留原有格式
            if (bodyPart) {
              // 计算正文部分在原文中的起始位置（考虑标题长度变化）
              const bodyStartInFull = lineStart + dotPos + 1 + titleLengthDiff;
              // 按西文/中文分组处理
              let currentIsWestern = null;
              let currentSubSeg = '';
              let currentPos = 0;
              
              for (const char of bodyPart) {
                const isWestern = this.isWesternChar(char);
                if (currentIsWestern === null) {
                  currentIsWestern = isWestern;
                  currentSubSeg = char;
                } else if (isWestern === currentIsWestern) {
                  currentSubSeg += char;
                } else {
                  // 保存当前段
                  const segmentStart = bodyStartInFull + currentPos;
                  if (currentIsWestern) {
                    const hasBold = currentSubSeg.split('').some((c, idx) => {
                      return charBoldMap[segmentStart + idx] || false;
                    });
                    textRuns.push(new TextRun({
                      text: currentSubSeg,
                      size: 32,
                      font: 'Times New Roman',
                      bold: hasBold
                    }));
                  } else {
                    for (let i = 0; i < currentSubSeg.length; i++) {
                      const charPos = segmentStart + i;
                      const originalBold = charBoldMap[charPos] || false;
                      textRuns.push(new TextRun({
                        text: currentSubSeg[i],
                        size: 32,
                        font: '仿宋_GB2312',
                        bold: originalBold
                      }));
                    }
                  }
                  currentPos += currentSubSeg.length;
                  currentIsWestern = isWestern;
                  currentSubSeg = char;
                }
              }
              // 保存最后一段
              if (currentSubSeg) {
                const segmentStart = bodyStartInFull + currentPos;
                if (currentIsWestern) {
                  const hasBold = currentSubSeg.split('').some((c, idx) => {
                    return charBoldMap[segmentStart + idx] || false;
                  });
                  textRuns.push(new TextRun({
                    text: currentSubSeg,
                    size: 32,
                    font: 'Times New Roman',
                    bold: hasBold
                  }));
                } else {
                  for (let i = 0; i < currentSubSeg.length; i++) {
                    const charPos = segmentStart + i;
                    const originalBold = charBoldMap[charPos] || false;
                    textRuns.push(new TextRun({
                      text: currentSubSeg[i],
                      size: 32,
                      font: '仿宋_GB2312',
                      bold: originalBold
                    }));
                  }
                }
              }
            }
          } else {
            // 整行都是标题
            textRuns.push(...this.createTextRunsWithFont(processedLine, {
              size: 32,
              font: currentFont,
              bold: currentBold
            }));
          }
        } else {
          // 普通正文 - 保留原有加粗格式
          // 按西文/中文分组处理
          let currentIsWestern = null;
          let currentSubSeg = '';
          let currentPos = 0;
          
          for (const char of processedLine) {
            const isWestern = this.isWesternChar(char);
            if (currentIsWestern === null) {
              currentIsWestern = isWestern;
              currentSubSeg = char;
            } else if (isWestern === currentIsWestern) {
              currentSubSeg += char;
            } else {
              // 保存当前段
              const segmentStart = lineStart + currentPos;
              if (currentIsWestern) {
                const hasBold = currentSubSeg.split('').some((c, idx) => {
                  return charBoldMap[segmentStart + idx] || false;
                });
                textRuns.push(new TextRun({
                  text: currentSubSeg,
                  size: 32,
                  font: 'Times New Roman',
                  bold: hasBold
                }));
              } else {
                for (let i = 0; i < currentSubSeg.length; i++) {
                  const charPos = segmentStart + i;
                  const originalBold = charBoldMap[charPos] || false;
                  textRuns.push(new TextRun({
                    text: currentSubSeg[i],
                    size: 32,
                    font: '仿宋_GB2312',
                    bold: originalBold
                  }));
                }
              }
              currentPos += currentSubSeg.length;
              currentIsWestern = isWestern;
              currentSubSeg = char;
            }
          }
          // 保存最后一段
          if (currentSubSeg) {
            const segmentStart = lineStart + currentPos;
            if (currentIsWestern) {
              const hasBold = currentSubSeg.split('').some((c, idx) => {
                return charBoldMap[segmentStart + idx] || false;
              });
              textRuns.push(new TextRun({
                text: currentSubSeg,
                size: 32,
                font: 'Times New Roman',
                bold: hasBold
              }));
            } else {
              for (let i = 0; i < currentSubSeg.length; i++) {
                const charPos = segmentStart + i;
                const originalBold = charBoldMap[charPos] || false;
                textRuns.push(new TextRun({
                  text: currentSubSeg[i],
                  size: 32,
                  font: '仿宋_GB2312',
                  bold: originalBold
                }));
              }
            }
          }
        }
        
        // 判断是否是标题段落
        const isTitleParagraph = currentLevel !== null;

        paragraphs.push(new Paragraph({
          style: isTitleParagraph ? `Heading${currentLevel}` : "BodyText",
          alignment: AlignmentType.JUSTIFIED,
          spacing: { line: 580, lineRule: "exact" }, // 固定行间距29磅，无段前段后间距
          indent: { firstLine: 640 }, // 首行缩进2字符（16pt字体 = 640 twips）
          children: textRuns
        }));
      }
    });

    return paragraphs;
  }

  /**
   * 生成公文文档
   */
  async generateDocument(data) {
    const { title, sender, content, signature, date } = data;
    const [year, month, day] = date.split('-');

    // 解析HTML内容为段落（使用新的解析逻辑）
    const paragraphs = this.parseHtmlContent(content);

    const doc = new Document({
      // 启用奇偶页不同的页眉页脚（必须在Document级别设置）
      evenAndOddHeaderAndFooters: true,
      // 定义文档样式
      styles: {
        default: {
          document: {
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32  // 16pt
            },
            paragraph: {
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,      // 29磅固定行距
                lineRule: "exact"
              }
            }
          }
        },
        paragraphStyles: [
          {
            id: "Normal",
            name: "Normal",
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32,  // 16pt
              bold: false
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,      // 29磅固定行距
                lineRule: "exact"
              }
            }
          },
          {
            id: "Title",
            name: "Title",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "方正小标宋简体",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 44,  // 22pt
              bold: false
            },
            paragraph: {
              alignment: AlignmentType.CENTER,
              indent: {
                firstLine: 0  // 不首行缩进
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              }
            }
          },
          {
            id: "Sender",
            name: "发文单位",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32  // 16pt
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 0  // 不首行缩进
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              }
            }
          },
          {
            id: "BodyText",
            name: "Body Text",
            basedOn: "Normal",
            next: "Normal",
            quickFormat: true,
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32,  // 16pt
              bold: false
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,      // 29磅固定行距
                lineRule: "exact"
              },
              widowControl: false  // 禁用孤行控制
            }
          },
          {
            id: "Signature",
            name: "Signature",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32  // 16pt
            },
            paragraph: {
              alignment: AlignmentType.CENTER,
              indent: {
                firstLine: 3200  // 首行缩进10字符
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              }
            }
          },
          {
            id: "Heading1",
            name: "Heading 1",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "黑体",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32,  // 16pt（三号字）
              bold: false
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              },
              widowControl: false  // 禁用孤行控制
            }
          },
          {
            id: "Heading2",
            name: "Heading 2",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "楷体_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32,  // 16pt（三号字）
              bold: true
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              },
              widowControl: false  // 禁用孤行控制
            }
          },
          {
            id: "Heading3",
            name: "Heading 3",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32,  // 16pt（三号字）
              bold: true
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              },
              widowControl: false  // 禁用孤行控制
            }
          },
          {
            id: "Heading4",
            name: "Heading 4",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32,  // 16pt（三号字）
              bold: true
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              },
              widowControl: false  // 禁用孤行控制
            }
          }
        ]
      },
      sections: [{
        properties: {
          page: {
            margin: {
              top: 2097,    // 上3.7cm
              right: 1474,  // 右2.6cm
              bottom: 1984, // 下3.5cm
              left: 1587    // 左2.8cm
            }
          }
        },
        // 页脚：奇数页右对齐，偶数页左对齐
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: "— ",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    text: " —",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  })
                ]
              })
            ]
          }),
          even: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.LEFT,
                children: [
                  new TextRun({
                    text: "— ",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    text: " —",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  })
                ]
              })
            ]
          })
        },
        children: [
          // 标题
          new Paragraph({
            style: "Title",
            alignment: AlignmentType.CENTER,
            spacing: { line: 580, lineRule: "exact" }, // 固定行间距29磅，无段前段后间距
            children: this.createTextRunsWithFont(title, {
              size: 44,
              font: "方正小标宋简体",
              bold: false // 公文标题不加粗
            })
          }),
          // 标题后空一行
          new Paragraph({
            spacing: { line: 580, lineRule: "exact" },
            children: []
          }),
          // 发文单位
          new Paragraph({
            style: "Sender",
            alignment: AlignmentType.JUSTIFIED, // 两端对齐
            spacing: { line: 580, lineRule: "exact" }, // 固定行间距29磅，无段前段后间距
            indent: { firstLine: 0 }, // 不首行缩进
            children: this.createTextRunsWithFont(sender + "：", { // 在发文单位后面添加冒号
              size: 32,
              font: "仿宋_GB2312"
            })
          }),
          // 正文内容
          ...paragraphs,
          // 署名前空两行
          new Paragraph({
            spacing: { line: 580, lineRule: "exact" },
            children: []
          }),
          new Paragraph({
            spacing: { line: 580, lineRule: "exact" },
            children: []
          }),
          // 署名
          new Paragraph({
            style: "Signature",
            alignment: AlignmentType.CENTER, // 居中对齐
            spacing: { line: 580, lineRule: "exact" }, // 固定行间距29磅，无段前段后间距
            indent: { firstLine: 3200 }, // 首行缩进10字符（16pt字体 = 3200 twips）
            children: this.createTextRunsWithFont(signature, {
              size: 32,
              font: "仿宋_GB2312"
            })
          }),
          // 日期
          new Paragraph({
            style: "Signature",
            alignment: AlignmentType.CENTER, // 居中对齐
            spacing: { line: 580, lineRule: "exact" }, // 固定行间距29磅，无段前段后间距
            indent: { firstLine: 3200 }, // 首行缩进10字符（16pt字体 = 3200 twips）
            children: this.createTextRunsWithFont(`${year}年${parseInt(month)}月${parseInt(day)}日`, {
              size: 32,
              font: "仿宋_GB2312"
            })
          })
        ]
      }]
    });

    // 生成并下载
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${title}.docx`);
  }

  /**
   * 生成行程安排文档（公文格式）
   */
  async generateSchedule(data) {
    const { title, scheduleText } = data;

    const lines = scheduleText.split('\n').filter(line => line.trim());
    const paragraphs = lines.map(line => {
      const isDateLine = line.includes('月') && line.includes('日') && (line.includes('（') || line.includes('('));
      const isScheduleItem = /^\d{1,2}:\d{2}--/.test(line.trim());
      
      if (isScheduleItem) {
        // 行程事项行：首行缩进2字符，后续行缩进3.32厘米
        // 实际校准：2558 twips显示为3.38厘米，目标3.32厘米
        // 计算：2558 * (3.32 / 3.38) ≈ 2513 twips
        return new Paragraph({
          style: "BodyText",
          alignment: AlignmentType.JUSTIFIED,
          spacing: { line: 580, lineRule: "exact" },
          indent: {
            left: 2513,        // 3.32厘米（根据实测校准）
            hanging: 1873      // 2513 - 640 = 1873 (首行缩进2字符)
          },
          children: this.createTextRunsWithFont(line.trim(), {
            size: 32,
            font: "仿宋_GB2312",
            bold: false
          })
        });
      } else {
        // 日期行：首行缩进2字符
        return new Paragraph({
          style: "BodyText",
          alignment: AlignmentType.JUSTIFIED,
          spacing: { line: 580, lineRule: "exact" },
          indent: { firstLine: 640 },
          children: this.createTextRunsWithFont(line.trim(), {
            size: 32,
            font: "仿宋_GB2312",
            bold: isDateLine
          })
        });
      }
    });

    const doc = new Document({
      evenAndOddHeaderAndFooters: true,
      sections: [{
        properties: {
          page: {
            margin: {
              top: 2097,    // 上3.7cm
              right: 1474,  // 右2.6cm
              bottom: 1984, // 下3.5cm
              left: 1587    // 左2.8cm
            }
          }
        },
        // 奇数页页脚（右对齐）
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: "— ",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    text: " —",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  })
                ]
              })
            ]
          }),
          // 偶数页页脚（左对齐）
          even: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.LEFT,
                children: [
                  new TextRun({
                    text: "— ",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    text: " —",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  })
                ]
              })
            ]
          })
        },
        children: [
          // 标题
          new Paragraph({
            style: "Title",
            alignment: AlignmentType.CENTER,
            spacing: { line: 580, lineRule: "exact" },
            children: this.createTextRunsWithFont(title || '行程安排', {
              size: 44,
              font: "方正小标宋简体",
              bold: false
            })
          }),
          // 标题后空一行
          new Paragraph({
            spacing: { line: 580, lineRule: "exact" },
            children: []
          }),
          ...paragraphs
        ]
      }]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${title || '行程安排'}.docx`);
  }

  /**
   * 根据名称长度获取字号（完全复刻后端逻辑）
   * @param {number} nameLength - 名称长度
   * @returns {number} - 字号（半磅单位）
   */
  getTableSignFontSize(nameLength) {
    const fontSizeMap = {
      2: 240,   // 2字 - 120pt
      3: 224,   // 3字 - 112pt
      4: 208,   // 4字 - 104pt
      5: 192,   // 5字 - 96pt
      6: 170,   // 6字 - 85pt
      7: 144,   // 7字 - 72pt
      8: 144,   // 8字 - 72pt
      9: 144,   // 9字 - 72pt
      10: 144,  // 10字 - 72pt
      11: 144,  // 11字 - 72pt
      12: 144,  // 12字 - 72pt
      13: 144,  // 13字 - 72pt
      14: 144,  // 14字 - 72pt
      15: 104   // 15字及以上 - 52pt
    };

    if (nameLength <= 2) {
      return fontSizeMap[2];
    } else if (nameLength >= 15) {
      return fontSizeMap[15];
    }
    return fontSizeMap[nameLength] || 144;
  }

  /**
   * 生成台签文档（完全复刻 TableSheet.docx 模板）
   */
  async generateTableSign(data) {
    const { names, fontName = '华文新魏', copiesPerName = 2 } = data;

    // 虚线边框设置（0.5磅虚线）
    const dashedBorder = {
      style: BorderStyle.DASHED,
      size: 4,           // 4 (1/8磅单位) = 0.5磅
      color: "000000"
    };

    const tableBorders = {
      top: dashedBorder,
      bottom: dashedBorder,
      left: dashedBorder,
      right: dashedBorder,
      insideHorizontal: dashedBorder,
      insideVertical: dashedBorder
    };

    // 生成表格行
    const rows = [];
    
    names.forEach(name => {
      // 处理两字人名：添加两个空格
      const actualName = name.replace('  ', '');  // 移除已有空格
      const actualLength = actualName.length;
      const displayName = actualLength === 2 ? `${actualName[0]}  ${actualName[1]}` : name;
      
      // 根据名称长度获取字号
      const fontSize = this.getTableSignFontSize(actualLength);
      
      // 每个名称生成指定份数
      for (let i = 0; i < copiesPerName; i++) {
        rows.push(
          new TableRow({
            height: {
              value: 4820,         // 8.51 cm
              rule: HeightRule.EXACT
            },
            children: [
              new TableCell({
                width: { size: 100, type: WidthType.PERCENTAGE },
                verticalAlign: VerticalAlign.CENTER,
                borders: tableBorders,
                margins: {
                  top: 0,
                  bottom: 0,
                  left: 108,       // 0.19 cm
                  right: 108
                },
                children: [
                  new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                      new TextRun({
                        text: displayName,
                        font: fontName,
                        size: fontSize
                      })
                    ]
                  })
                ]
              })
            ]
          })
        );
      }
    });

    // 创建文档
    const doc = new Document({
      // 定义文档样式
      styles: {
        default: {
          document: {
            run: {
              font: fontName
            }
          }
        }
      },
      sections: [{
        properties: {
          page: {
            size: {
              width: 11907,     // 21.00 cm (A4宽度)
              height: 16840     // 29.70 cm (A4高度)
            },
            margin: {
              top: 284,         // 0.50 cm
              right: 284,       // 0.50 cm
              bottom: 284,      // 0.50 cm
              left: 284,        // 0.50 cm
              header: 851,      // 1.50 cm
              footer: 992       // 1.74 cm
            }
          }
        },
        children: [
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: tableBorders,
            rows: rows,
            alignment: AlignmentType.CENTER  // 表格居中对齐
          })
        ]
      }]
    });

    const blob = await Packer.toBlob(doc);
    const timestamp = new Date().toISOString().slice(0, 10);
    saveAs(blob, `台签_${timestamp}.docx`);
  }

  /**
   * 生成发文单位文档
   */
  async generateUnitDocument(data) {
    const { text, title = '各有关单位名单' } = data;

    const doc = new Document({
      // 启用奇偶页不同的页眉页脚
      evenAndOddHeaderAndFooters: true,
      // 定义文档样式
      styles: {
        default: {
          document: {
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32  // 16pt
            },
            paragraph: {
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,      // 29磅固定行距
                lineRule: "exact"
              }
            }
          }
        },
        paragraphStyles: [
          {
            id: "Normal",
            name: "Normal",
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32,  // 16pt
              bold: false
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,      // 29磅固定行距
                lineRule: "exact"
              }
            }
          },
          {
            id: "UnitTitle",
            name: "Unit Title",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "方正小标宋简体",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 44,  // 22pt
              bold: false
            },
            paragraph: {
              alignment: AlignmentType.CENTER,
              indent: {
                firstLine: 0  // 不首行缩进
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              }
            }
          },
          {
            id: "Attachment",
            name: "Attachment",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32  // 16pt
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              },
              widowControl: false  // 禁用孤行控制
            }
          },
          {
            id: "UnitBody",
            name: "Unit Body",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32  // 16pt
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              },
              widowControl: false  // 禁用孤行控制
            }
          },
          {
            id: "Heading1",
            name: "Heading 1",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "黑体",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32,  // 16pt（三号字）
              bold: false
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              },
              widowControl: false  // 禁用孤行控制
            }
          },
          {
            id: "Heading2",
            name: "Heading 2",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "楷体_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32,  // 16pt（三号字）
              bold: true
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              },
              widowControl: false  // 禁用孤行控制
            }
          },
          {
            id: "Heading3",
            name: "Heading 3",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32,  // 16pt（三号字）
              bold: true
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              },
              widowControl: false  // 禁用孤行控制
            }
          },
          {
            id: "Heading4",
            name: "Heading 4",
            basedOn: "Normal",
            next: "Normal",
            run: {
              font: {
                eastAsia: "仿宋_GB2312",
                ascii: "Times New Roman",
                hAnsi: "Times New Roman",
                cs: "Times New Roman"
              },
              size: 32,  // 16pt（三号字）
              bold: true
            },
            paragraph: {
              alignment: AlignmentType.JUSTIFIED,
              indent: {
                firstLine: 640  // 首行缩进2字符
              },
              spacing: {
                line: 580,
                lineRule: "exact"
              },
              widowControl: false  // 禁用孤行控制
            }
          }
        ]
      },
      sections: [{
        properties: {
          page: {
            margin: {
              top: 2097,    // 上3.7cm
              right: 1474,  // 右2.6cm
              bottom: 1984, // 下3.5cm
              left: 1587    // 左2.8cm
            }
          }
        },
        // 页脚：奇数页右对齐，偶数页左对齐
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: "— ",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    text: " —",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  })
                ]
              })
            ]
          }),
          even: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.LEFT,
                children: [
                  new TextRun({
                    text: "— ",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  }),
                  new TextRun({
                    text: " —",
                    size: 28,
                    font: {
                      eastAsia: "仿宋_GB2312",
                      ascii: "Times New Roman",
                      hAnsi: "Times New Roman",
                      cs: "Times New Roman"
                    }
                  })
                ]
              })
            ]
          })
        },
        children: [
          // 附件说明
          new Paragraph({
            style: "Attachment",
            alignment: AlignmentType.JUSTIFIED, // 两端对齐
            spacing: { line: 580, lineRule: "exact" }, // 固定行间距29磅
            indent: { firstLine: 0 }, // 不首行缩进
            children: this.createTextRunsWithFont("附件 1", {
              size: 32,
              font: "仿宋_GB2312"
            })
          }),
          // 标题
          new Paragraph({
            style: "UnitTitle",
            alignment: AlignmentType.CENTER,
            spacing: { line: 580, lineRule: "exact" }, // 固定行间距29磅
            children: this.createTextRunsWithFont(title, {
              size: 44,
              font: "方正小标宋简体",
              bold: false // 标题不加粗
            })
          }),
          // 标题后空一行
          new Paragraph({
            spacing: { line: 580, lineRule: "exact" },
            children: []
          }),
          // 正文内容
          new Paragraph({
            style: "UnitBody",
            alignment: AlignmentType.JUSTIFIED, // 两端对齐
            spacing: { line: 580, lineRule: "exact" }, // 固定行间距29磅
            indent: { firstLine: 640 }, // 首行缩进2字符（16pt字体 = 640 twips）
            children: this.createTextRunsWithFont(text, {
              size: 32,
              font: "仿宋_GB2312"
            })
          })
        ]
      }]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${title}.docx`);
  }
}

// 导出单例
const wordGenerator = new WordGeneratorService();
export default wordGenerator;
